/**
 * `name` — the sticky first column (M2 §5.1, §6).
 *
 * The row's hover affordances (expand chevron, open-item, chat bubble) belong to the row, not
 * to this cell: `GridRow` draws them around it `[B3§8.1]`. What the type owns is the text, the
 * inline editor and the clipboard pair.
 */
import { getColumnType } from '@elizmonday/shared';
import { NAME_COLUMN_DEFAULT_WIDTH } from '../../table/layout';
import { CellText, InlineTextEditor } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { NameSettingsDialog } from './SettingsDialog';

const shared = getColumnType('name');

export const nameColumnUi = defineColumnUi<string, Record<string, unknown>>({
  type: 'name',
  icon: 'columnName',
  gallery: { group: 'essentials', keywords: ['name', 'title', 'item'] },
  /**
   * The **sticky region**, not the cell: `name.width` is measured from the grid's left edge
   * and contains the 6 px group bar, the 33 px checkbox rail and the 65 px conversation cell
   * (P-021). 698 = 6 + 33 + 594 + 65 on `[ui:library/table-01-top.png]` (x 391 -> 1088), whose
   * stored width was 698; `table/layout.ts` turns it back into the 594 px cell — and owns the
   * number, so the grid and the registry cannot disagree about it.
   */
  defaultWidth: NAME_COLUMN_DEFAULT_WIDTH,
  align: 'left',
  groupable: true,

  Cell: ({ value, item, context }) => (
    <CellText text={value ?? item.name} align="left" search={context.search} />
  ),

  Editor: ({ value, item, initialInput, autoFocus, onCommit, onCancel, onNavigate }) => (
    <InlineTextEditor
      initial={value ?? item.name}
      initialInput={initialInput}
      autoFocus={autoFocus}
      ariaLabel="Item name"
      // B3 §8.5: empty is rejected and reverts, it never clears the name.
      onCommit={(next) => (next.trim() === '' ? onCancel() : onCommit(next))}
      onCancel={onCancel}
      onNavigate={onNavigate}
    />
  ),

  SettingsDialog: NameSettingsDialog,

  fromClipboard(text) {
    return text.trim() === '' ? null : text;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('name'),
});
